//@flow
import React from 'react';
import { createRefetchContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import getter from 'dibs-object-getter';
import GearIcon from 'dibs-icons/exports/GearFilled';
import { ItemTile } from 'dibs-search-product-tile/exports/ItemTile';
import { getBuyerId, getUserType } from 'dibs-cookie-jar';
import { HeartWrapper } from 'dibs-portfolio-heart';
import { FolderWrapper } from 'dibs-portfolio-folder';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import { OverlaySpinner } from 'dibs-elements/exports/OverlaySpinner';

import { Button } from 'dibs-elements/exports/Button';
import { Tooltip } from 'dibs-elements/exports/Tooltip';

import { favoriteItemLogin } from '../../authInterface';
import { FavoritesProvider } from '../../shared/favorites/TradeFavoritesProvider';
import { ConnectFolderModal } from './ConnectFolderModal/ConnectFolderModal';
import { loadAddToFolderModalWrapper } from '../../shared/loadAddToFolderModalWrapper';
import { logError } from '../../helpers';
import { QuickViewContext, QuickViewProvider } from './QuickViewProvider';
import { Pagination } from '../../shared/Pagination';

import styles from './FavoritesWrapper.scss';

import type { RelayRefetchProp } from 'react-relay';
import type { FavoritesWrapper_viewer as Viewer } from './__generated__/FavoritesWrapper_viewer.graphql';
import { trackEvent, trackEcomEvent } from '../../shared/helpers/tracking';
import { createProductImpressionTracking, trackItemTile } from '../helpers/designProfileTracking';

type Props = {|
    isEditMode: boolean,
    isTrade: boolean,
    relay?: RelayRefetchProp,
    viewer: ?Viewer,
    isMobile: boolean,
|};

type State = {|
    currentPage: number,
    isLoading: boolean,
    showConnectFolderModal: boolean,
    showEditMenu: boolean,
|};

const ConnectAnotherFolder = () => (
    <FormattedMessage
        id="abt.designProfile.FavoritesWrapper.connectAnotherFolder"
        defaultMessage="Connect Another Folder"
    />
);

const folderTrackingHooks = {
    trackFolderClick() {
        trackEvent({
            category: 'item favoriting',
            action: 'item favoriting folder clicked',
            label: 'designer favorites',
        });
    },
    trackItemAdded() {
        trackEvent({
            category: 'item favoriting',
            action: 'item added into folder',
            label: 'designer favorites',
        });
    },
};

const trackHeart = (wasAdded: boolean) => {
    const category = 'item favoriting';
    const action = `item ${wasAdded ? 'added' : 'removed'}`;
    const label = 'designer favorites';
    trackEvent({
        category,
        action,
        label,
    });
};

export class FavoritesWrapperComponent extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            currentPage: 1,
            isLoading: false,
            showConnectFolderModal: false,
            showEditMenu: false,
        };
    }

    getTradeFirm = () => {
        const { isEditMode, viewer } = this.props;
        const tradeFirmsKey = isEditMode ? 'tradeFirmsEditMode' : 'tradeFirmsDisplayMode';
        const [tradeFirm] = getter(viewer).value(tradeFirmsKey) || [];
        return {
            ...tradeFirm,
            members: (tradeFirm && tradeFirm.members) || [], // for flow
        };
    };

    refreshFavorites = () => {
        const { relay } = this.props;
        const isTrade = getUserType(document.cookie) === 'trade';

        if (relay) {
            this.setState({ isLoading: true }, () => {
                relay.refetch(
                    fragmentVariables => ({
                        ...fragmentVariables,
                        isTrade,
                    }),
                    null,
                    (error: ?Error) => {
                        if (error) {
                            logError(error);
                        }
                        this.setState({ isLoading: false });
                    },
                    { force: true }
                );
            });
        }
    };

    handlePageChange = (newPage: number) => {
        if (this.props.relay) {
            this.setState({ currentPage: newPage, isLoading: true }, () => {
                this.props.relay.refetch(
                    fragmentVariables => {
                        return {
                            ...fragmentVariables,
                            favoritesPage: newPage,
                        };
                    },
                    null,
                    (error: ?Error) => {
                        if (error) {
                            logError(error);
                        }
                        this.setState({ isLoading: false });
                    },
                    { force: true }
                );
            });
        }
    };

    launchFolderLogin = (item: ?Object): Promise<void> => {
        return new Promise((resolve, reject) => {
            favoriteItemLogin({
                item,
                onSuccess: ({ userId }) => {
                    loadAddToFolderModalWrapper({ userId }).then(resolve);
                    this.refreshFavorites();
                },
                onError: reject,
                shouldPauseAuthFlow: state => state.step === 0,
            });
        });
    };

    launchHeartLogin = (item: ?Object, cb: ?Function) => {
        const onSuccess = () => {
            if (cb) {
                cb();
            }
            this.refreshFavorites();
        };

        favoriteItemLogin({
            item,
            onSuccess,
        });
    };

    onFolderSelection = () => {
        this.setState({ showConnectFolderModal: false }, this.refreshFavorites);
    };

    renderConnectFolderModal = () => {
        const tradeFirm = this.getTradeFirm();
        const firmMembers = (getter(tradeFirm).value('members') || []).map(member =>
            getter(member).value('userId')
        );
        const firmId = getter(tradeFirm).value('serviceId');

        return (
            <ConnectFolderModal
                onClose={() => this.setState({ showConnectFolderModal: false })}
                firmMembers={firmMembers}
                firmId={firmId}
                onFolderSelection={this.onFolderSelection}
            />
        );
    };

    renderEditUI = () => {
        const { showEditMenu } = this.state;

        return (
            <div
                className={styles.connectNewFolder}
                data-tn="open-edit-menu-button"
                onClick={() => this.setState({ showEditMenu: true })}
            >
                <GearIcon />
                {showEditMenu && (
                    <Tooltip
                        align="center"
                        closeOnOutsideClick
                        direction="left"
                        isVisible
                        onClose={() => this.setState({ showEditMenu: false })}
                    >
                        <div
                            className={styles.tooltipContent}
                            data-tn="show-connect-folder-modal-cta"
                            onClick={() => {
                                this.setState({
                                    showConnectFolderModal: true,
                                    showEditMenu: false,
                                });
                            }}
                        >
                            <ConnectAnotherFolder />
                        </div>
                    </Tooltip>
                )}
            </div>
        );
    };

    renderNoFolderView() {
        const { isEditMode } = this.props;

        if (!isEditMode) {
            return null;
        }

        return (
            <div className={styles.noItemsContainer}>
                <div className={styles.noItemsContent}>
                    <div className={styles.noItemsMessage} data-tn="no-folder-message">
                        <FormattedMessage
                            id="abt.designProfile.FavoritesWrapper.noFolderMessage"
                            defaultMessage="Display pieces you love here by connecting to one of the folders in the Favorites section of your 1stdibs account."
                        />
                    </div>
                    <div className={styles.connectDifferentFolderButton}>
                        <Button
                            dataTn="link-different-folder-button-no-folder"
                            onClick={() => {
                                this.setState({
                                    showConnectFolderModal: true,
                                    showEditMenu: false,
                                });
                            }}
                        >
                            <FormattedMessage
                                id="abt.designProfile.FavoritesWrapper.connectFavorites"
                                defaultMessage="connect to favorites"
                            />
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    renderNoItemsView = () => {
        const { isEditMode } = this.props;
        const folderName =
            getter(this.getTradeFirm())
                .at('publicProfile')
                .at('portfoliosInflated')
                .at(0)
                .value('name') || '';

        const message = isEditMode ? (
            <FormattedMessage
                id="abt.designProfile.FavoritesWrapper.noItemsEditMode"
                defaultMessage='Folder "{folderName}" contains no items'
                values={{ folderName }}
            />
        ) : (
            <FormattedMessage
                id="abt.designProfile.FavoritesWrapper.noItemsDisplayMode"
                defaultMessage="Folder contains no items"
            />
        );

        return (
            <div className={styles.noItemsContainer}>
                <div className={styles.noItemsContent}>
                    <div className={styles.noItemsMessage} data-tn="no-items-message">
                        {message}
                    </div>
                    {isEditMode && (
                        <div className={styles.connectDifferentFolderButton}>
                            <Button
                                dataTn="link-different-folder-button-no-items"
                                onClick={() => {
                                    this.setState({
                                        showConnectFolderModal: true,
                                        showEditMenu: false,
                                    });
                                }}
                            >
                                <ConnectAnotherFolder />
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    getPortfolioInfo() {
        const [portfolio] =
            getter(this.getTradeFirm())
                .at('publicProfile')
                .value('portfoliosInflated') || [];

        const portfolioItems = (
            getter(portfolio)
                .at('items')
                .value('edges') || []
        )
            .map(edge =>
                getter(edge)
                    .at('node')
                    .value('item')
            )
            .filter(Boolean);

        return {
            portfolio,
            portfolioItems,
        };
    }

    render() {
        const { isEditMode, isTrade, relay, isMobile } = this.props;
        const { currentPage, isLoading, showConnectFolderModal } = this.state;

        if (!relay) {
            const messages = [
                'Tried to render FavoritesWrapper without relay and/or relay environment',
                `relay = ${JSON.stringify(relay)}`,
                relay ? `environment = ${JSON.stringify(relay.environment)}` : '',
            ];
            logError(messages.join('. '));
            return null;
        }

        const { portfolio, portfolioItems } = this.getPortfolioInfo();
        const numberOfPages =
            getter(portfolio)
                .at('items')
                .value('numberOfPages') || 0;
        const totalResults =
            getter(portfolio)
                .at('items')
                .value('totalResults') || 0;

        const itemIds = portfolioItems.map(item => getter(item).value('serviceId')).filter(Boolean);

        const userId = getBuyerId(document.cookie);

        let mainContent;
        if (!portfolio && isEditMode) {
            mainContent = this.renderNoFolderView();
        } else if (!portfolioItems.length) {
            mainContent = this.renderNoItemsView();
        } else {
            mainContent = (
                <FavoritesProvider
                    environment={getter(relay).value('environment')}
                    fetchFolder
                    fetchHeart
                    itemIds={itemIds}
                    userId={userId}
                >
                    {({ props: providedProps }) => {
                        return (
                            <QuickViewProvider>
                                <QuickViewContext.Consumer>
                                    {({ openQuickView, closeQuickView, activeQuickView }) => {
                                        return portfolioItems.filter(Boolean).map((item, index) => {
                                            const favoritesComponent = (
                                                <div className={styles.favoritesIconsWrap}>
                                                    <div className={styles.favoritesIconContainer}>
                                                        <HeartWrapper
                                                            authModalShow={cb =>
                                                                this.launchHeartLogin(item, cb)
                                                            }
                                                            itemId={item.serviceId}
                                                            onFavorited={() => trackHeart(true)}
                                                            onUnFavorited={() => trackHeart(false)}
                                                            userId={userId}
                                                            viewer={providedProps.viewer}
                                                        />
                                                    </div>
                                                    {!isMobile && (
                                                        <div
                                                            className={
                                                                styles.favoritesIconContainer
                                                            }
                                                        >
                                                            <FolderWrapper
                                                                environment={relay.environment}
                                                                getModalComponent={
                                                                    userId
                                                                        ? loadAddToFolderModalWrapper
                                                                        : this.launchFolderLogin
                                                                }
                                                                loadPortfolioItemData
                                                                primaryItemPk={item.serviceId}
                                                                selectedItemIds={[item.serviceId]}
                                                                shouldFillInFolder={!!userId}
                                                                singlePortfolioItem
                                                                userId={userId}
                                                                userIds={[userId]}
                                                                viewer={providedProps.viewer}
                                                                trackingHooks={folderTrackingHooks}
                                                                onAddItemSuccess={() => {
                                                                    if (
                                                                        authModalLoader.isPaused()
                                                                    ) {
                                                                        authModalLoader.resume();
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                            return (
                                                <div
                                                    className={styles.tileWrapper}
                                                    key={item.serviceId}
                                                >
                                                    <CurrencyProvider
                                                        render={({ currency }) => (
                                                            <ItemTile
                                                                index={index}
                                                                item={item}
                                                                itemsPerRow={4}
                                                                showSellerName={false}
                                                                showCreatorName={false}
                                                                currency={currency}
                                                                isMobile={isMobile}
                                                                onContentVisible={() => {
                                                                    if (
                                                                        providedProps.viewer !==
                                                                        null
                                                                    ) {
                                                                        trackEcomEvent({
                                                                            type:
                                                                                'productImpression',
                                                                            products: createProductImpressionTracking(
                                                                                {
                                                                                    isTrade,
                                                                                    items: [item],
                                                                                    positionNum: index,
                                                                                }
                                                                            ),
                                                                        });
                                                                    }
                                                                }}
                                                                onClick={() => {
                                                                    const products = createProductImpressionTracking(
                                                                        {
                                                                            isTrade,
                                                                            items: [item],
                                                                            positionNum: index,
                                                                        }
                                                                    );
                                                                    if (products.length) {
                                                                        trackItemTile(products[0]);
                                                                    }
                                                                }}
                                                                disableHover
                                                                showMeasurements={false}
                                                                showMSKUSwatch={false}
                                                                activeQuickView={activeQuickView}
                                                                openQuickView={openQuickView}
                                                                closeQuickView={closeQuickView}
                                                                viewer={null}
                                                                favoritesComponent={
                                                                    favoritesComponent
                                                                }
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            );
                                        });
                                    }}
                                </QuickViewContext.Consumer>
                            </QuickViewProvider>
                        );
                    }}
                </FavoritesProvider>
            );
        }

        return (
            <div className={`${styles.tilesContainer} rowFlex primary-page-width`}>
                {isEditMode && portfolio && this.renderEditUI()}
                {mainContent}
                <div className={styles.paginationRow}>
                    <Pagination
                        handlePaginate={this.handlePageChange}
                        numberOfPages={numberOfPages}
                        page={currentPage}
                        totalResults={totalResults}
                    />
                </div>
                {showConnectFolderModal && this.renderConnectFolderModal()}
                {isLoading && <OverlaySpinner />}
            </div>
        );
    }
}

export const FavoritesWrapper = createRefetchContainer(
    FavoritesWrapperComponent,
    {
        viewer: graphql`
            fragment FavoritesWrapper_viewer on Viewer {
                tradeFirmsDisplayMode: tradeFirms(urlLabel: $firmUrlLabel) @skip(if: $isEditMode) {
                    serviceId
                    publicProfile {
                        portfoliosInflated {
                            items(first: 60, page: $favoritesPage) {
                                numberOfPages
                                totalResults
                                edges {
                                    node {
                                        item {
                                            ...ItemTile_item @arguments(isTrade: $isTrade)
                                            serviceId
                                            firstPhotoWebPath(size: thumb)
                                            title
                                            seller {
                                                serviceId
                                            }
                                            isContemporary
                                            isCustomizable
                                            isNewAndMadeToOrder
                                            classification {
                                                categories {
                                                    category {
                                                        key
                                                        browseUrl
                                                    }
                                                }
                                            }
                                            pricing {
                                                amount {
                                                    amount
                                                }
                                                netPrice {
                                                    amount
                                                }
                                                salePrice {
                                                    amount
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            name
                        }
                    }
                }
                tradeFirmsEditMode: tradeFirms(urlLabel: $firmUrlLabel) @include(if: $isEditMode) {
                    serviceId
                    members {
                        userId
                    }
                    publicProfile {
                        portfoliosInflated {
                            items(first: 60, page: $favoritesPage) {
                                numberOfPages
                                totalResults
                                edges {
                                    node {
                                        item {
                                            ...ItemTile_item @arguments(isTrade: $isTrade)
                                            serviceId
                                            firstPhotoWebPath(size: thumb)
                                            title
                                            seller {
                                                serviceId
                                            }
                                            isContemporary
                                            isCustomizable
                                            isNewAndMadeToOrder
                                            classification {
                                                categories {
                                                    category {
                                                        key
                                                        browseUrl
                                                    }
                                                }
                                            }
                                            pricing {
                                                amount {
                                                    amount
                                                }
                                                netPrice {
                                                    amount
                                                }
                                                salePrice {
                                                    amount
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            name
                        }
                    }
                }
            }
        `,
    },
    graphql`
        query FavoritesWrapperRefetchQuery(
            $firmUrlLabel: String!
            $isEditMode: Boolean!
            $isTrade: Boolean!
            $favoritesPage: Int!
        ) {
            viewer {
                ...FavoritesWrapper_viewer
            }
        }
    `
);
